import { useMutation } from "@tanstack/react-query"
import type {
  UseMutationOptions,
  UseMutationResult,
} from "@tanstack/react-query"

import client from "~/backend/axiosClient"

import type {
  UpsertCustomFieldValueMutationRequest,
  UpsertCustomFieldValueMutationResponse,
} from "../types/UpsertCustomFieldValue"

type UpsertCustomFieldValueClient = typeof client<
  UpsertCustomFieldValueMutationResponse,
  Error,
  UpsertCustomFieldValueMutationRequest
>
type UpsertCustomFieldValue = {
  data: UpsertCustomFieldValueMutationResponse
  error: Error
  request: UpsertCustomFieldValueMutationRequest
  pathParams: never
  queryParams: never
  headerParams: never
  response: Awaited<ReturnType<UpsertCustomFieldValueClient>>
  client: {
    parameters: Partial<Parameters<UpsertCustomFieldValueClient>[0]>
    return: Awaited<ReturnType<UpsertCustomFieldValueClient>>
  }
}
/**
 * @summary Upsert a custom field value
 * @link /v1/custom-field/upsert-custom-field-value
 */
export function useUpsertCustomFieldValue(
  options: {
    mutation?: UseMutationOptions<
      UpsertCustomFieldValue["response"],
      UpsertCustomFieldValue["error"],
      UpsertCustomFieldValue["request"]
    >
    client?: UpsertCustomFieldValue["client"]["parameters"]
  } = {}
): UseMutationResult<
  UpsertCustomFieldValue["response"],
  UpsertCustomFieldValue["error"],
  UpsertCustomFieldValue["request"]
> {
  const { mutation: mutationOptions, client: clientOptions = {} } =
    options ?? {}
  return useMutation<
    UpsertCustomFieldValue["response"],
    UpsertCustomFieldValue["error"],
    UpsertCustomFieldValue["request"]
  >({
    mutationFn: async (data) => {
      const res = await client<
        UpsertCustomFieldValue["data"],
        UpsertCustomFieldValue["error"],
        UpsertCustomFieldValue["request"]
      >({
        method: "put",
        url: `/v1/custom-field/upsert-custom-field-value`,
        data,
        ...clientOptions,
      })
      return res
    },
    ...mutationOptions,
  })
}
